import { useEffect, useState } from 'react';
import {
  File as FileIcon,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  Paperclip,
  Presentation,
} from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import {
  documentKind,
  formatFileSize,
  type DocumentKind,
  type DocumentRow,
  type DocumentsClient,
} from '@jeraisonne/documents';

const SIGNED_URL_TTL_SECONDS = 3600;

const KIND_ICON: Record<DocumentKind, typeof FileIcon> = {
  image: ImageIcon,
  pdf: FileText,
  word: FileText,
  text: FileText,
  sheet: FileSpreadsheet,
  slides: Presentation,
  other: FileIcon,
};

type CardState =
  | { status: 'loading' }
  | { status: 'deleted' }
  | { status: 'error' }
  | { status: 'ready'; doc: DocumentRow; url: string };

/**
 * Carte d'un fichier partagé dans le chat (message `source='file'`, migration
 * 0262). LECTURE seule : un invité anonyme de la salle la voit et l'ouvre — la
 * RLS de `documents` (via `is_visio_room_member`) lui donne le droit de lire la
 * ligne ET l'objet Storage.
 *
 * L'URL signée est récupérée dès le montage (1 h) et posée dans un vrai `<a>` :
 * un `window.open` après un `await` serait bloqué par Safari (geste perdu).
 * Une image s'affiche en miniature ; le reste en ligne « icône + nom + taille ».
 */
export function ChatFileCard({
  documentId,
  fallbackName,
  mine,
  documents,
}: {
  documentId: string | null;
  /** Nom du fichier tel qu'enregistré dans le message (`body`) — repli tant que la ligne charge. */
  fallbackName: string;
  mine: boolean;
  documents: DocumentsClient;
}) {
  const [state, setState] = useState<CardState>(
    documentId ? { status: 'loading' } : { status: 'deleted' },
  );

  useEffect(() => {
    if (!documentId) {
      setState({ status: 'deleted' });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    void (async () => {
      try {
        const doc = await documents.getById(documentId);
        if (cancelled) return;
        if (!doc) {
          setState({ status: 'deleted' });
          return;
        }
        const url = await documents.signedUrl(doc.storagePath, {
          ttlSeconds: SIGNED_URL_TTL_SECONDS,
        });
        if (!cancelled) setState({ status: 'ready', doc, url });
      } catch (err) {
        console.error('[chat] fichier indisponible', err);
        if (!cancelled) setState({ status: 'error' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [documentId, documents]);

  const muted = mine ? 'text-white/70' : 'text-ink-500';

  if (state.status === 'deleted') {
    return (
      <span className={cn('flex items-center gap-1.5 italic', muted)}>
        <Paperclip className="size-3.5 shrink-0" aria-hidden />
        Fichier supprimé
      </span>
    );
  }

  if (state.status !== 'ready') {
    return (
      <span className="flex items-center gap-1.5">
        <Paperclip className={cn('size-3.5 shrink-0', muted)} aria-hidden />
        <span className="break-words">{fallbackName}</span>
        <span className={cn('text-xs', muted)}>
          {state.status === 'loading' ? '…' : '(indisponible)'}
        </span>
      </span>
    );
  }

  const { doc, url } = state;
  const kind = documentKind(doc.mimeType);
  const Icon = KIND_ICON[kind];

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex min-h-[2.75rem] min-w-0 flex-col justify-center gap-1.5 active:scale-[0.98]"
    >
      {kind === 'image' && (
        <img
          src={url}
          alt={doc.fileName}
          loading="lazy"
          className="max-h-44 w-full rounded-lg object-cover"
        />
      )}
      <span className="flex min-w-0 items-center gap-2">
        <Icon className="size-4 shrink-0" aria-hidden />
        <span className="min-w-0 break-words underline underline-offset-2">{doc.fileName}</span>
        <span className={cn('shrink-0 text-xs', muted)}>{formatFileSize(doc.sizeBytes)}</span>
      </span>
    </a>
  );
}
